import React, { useState } from 'react';
import { useMedia } from '@drivekyte/ui';
import dynamic from 'next/dynamic';
import {
  NavbarVariantColorEnum,
  NavbarVariantStateEnum,
} from '@/constants/navbar';
import useAnalyticsWithSource from '@/hooks/use-analytics-with-source';
import useAuthentication from '@/hooks/use-authentication';
import useMe from '@/hooks/use-me';
import useScroll from '@/hooks/use-scroll';
import useSignOut from '@/hooks/use-sign-out';
import { ScreenHint } from '@/types/auth';
import DesktopNavBar from './desktop-nav-bar/desktop-nav-bar';
import MobileNavBarCollapsed from './mobile-nav-bar/mobile-nav-bar-collapsed';
import { NavbarProvider } from './nav-bar-context';
import SimpleNavBar from './simple-nav-bar/simple-nav-bar';

const MobileNavBarExpanded = dynamic(() =>
  import('./mobile-nav-bar/mobile-nav-bar-expanded'),
);

type NavbarProps = {
  navState?: NavbarVariantStateEnum;
  color?: NavbarVariantColorEnum;
};

const NavBar = ({
  navState = NavbarVariantStateEnum.DEFAULT,
  color = NavbarVariantColorEnum.DARK,
}: NavbarProps) => {
  const media = useMedia();
  const handleAuth = useAuthentication();
  const { trackEvent, AnalyticsEvents } = useAnalyticsWithSource();
  const signOutMutation = useSignOut();
  const { isAuthenticated } = useMe();
  const [expanded, setExpanded] = useState(false);
  const handlePress = () => setExpanded((currentValue) => !currentValue);
  const [top, setTop] = useState(0);
  const isDark = color === NavbarVariantColorEnum.DARK;
  const isSimpleNavState = navState === NavbarVariantStateEnum.SIMPLE;

  // Remove when useMedia works correctly!
  // 1040 is the breakpoint for gtMd
  const isMobile = !media.gtMd || window.innerWidth < 1040;

  const handlePressSignOut = (event: React.MouseEvent) => {
    event.preventDefault();
    trackEvent(AnalyticsEvents.Navigation.NavSignOutButtonClicked);
    signOutMutation.mutate();
  };

  const handleSignUp = (event: React.MouseEvent, screenHint?: ScreenHint) => {
    event.preventDefault();
    trackEvent(AnalyticsEvents.Navigation.NavSignUpButtonClicked);
    handleAuth(screenHint);
  };

  const checkBranchBanner = (scrollPosition: number) => {
    let branchBanner: HTMLElement | null = null;
    branchBanner = document.getElementById('branch-banner-iframe');
    if (branchBanner && scrollPosition < 30) {
      const height = branchBanner.clientHeight;
      setTop(height);
    } else {
      setTop(0);
    }
  };

  useScroll(checkBranchBanner, true);

  return (
    <NavbarProvider
      isAuthenticated={isAuthenticated}
      handleSignUp={handleSignUp}
      handlePressSignOut={handlePressSignOut}
      navState={navState}
      isDark={isDark}
    >
      <nav>
        {isSimpleNavState ? (
          <SimpleNavBar />
        ) : (
          <>
            {!isMobile && <DesktopNavBar />}
            {isMobile &&
              (expanded ? (
                <MobileNavBarExpanded onPress={handlePress} top={top} />
              ) : (
                <MobileNavBarCollapsed onPress={handlePress} top={top} />
              ))}
          </>
        )}
      </nav>
    </NavbarProvider>
  );
};

export default NavBar;
